import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { FaBook, FaEye, FaPlus } from "react-icons/fa";
import styles from "./Book.module.css";
import { useSelector } from "react-redux";
import AxiosInstance, { TokenService } from "../../../services/Api";
import { CustomButton } from "../../../components";
import CustomMRTTable from "../../../components/CustomMRTTable/CustomMRTTable";
import PopupModal from "../../../components/PopupModal/PopupModal";

// ─────────────────────────────────────────────
//  Map MRT column accessorKey → API payload key
//  KEY = column accessorKey, VALUE = API field name
// ─────────────────────────────────────────────
const FILTER_KEY_MAP = {
  class: "class_id",
  subject: "subject",
  medium: "medium",
  term: "term",
  volume: "volume",
  total_pages: "total_page",
};

const EMPTY_FILTERS = {
  class_id: "",
  subject: "",
  medium: "",
  term: "",
  volume: "",
  total_page: "",
};

const BookList = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Global search: searchInput = typed (UI), apiSearch = sent (on Enter)
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");

  // Column filters — MRT native format: [{ id: "class", value: "X" }, ...]
  const [columnFilters, setColumnFilters] = useState([]);

  // Ref keeps latest filters available to the Enter-key listener
  const columnFiltersRef = useRef([]);
  useEffect(() => {
    columnFiltersRef.current = columnFilters;
  }, [columnFilters]);

  // Build API filter payload from an MRT filters array
  const buildFilterPayload = useCallback((filters) => {
    const payload = { ...EMPTY_FILTERS };
    (filters || []).forEach((filter) => {
      const apiKey = FILTER_KEY_MAP[filter.id];
      if (apiKey && filter.value != null && filter.value !== "") {
        payload[apiKey] = filter.value;
      }
    });
    return payload;
  }, []);

  // ─────────────────────────────────────────────
  //  Fetch API
  // ─────────────────────────────────────────────
  const fetchBookList = useCallback(
    async (filterOverrides, searchOverride) => {
      setLoading(true);
      try {
        const filters = buildFilterPayload(
          filterOverrides || columnFiltersRef.current,
        );
        const searchValue =
          searchOverride !== undefined ? searchOverride : apiSearch;

        const payload = {
          school_id: school_id,
          ...filters,
          search: searchValue,
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
        };

        const response = await AxiosInstance.post("/book/list", payload);

        if (response?.data?.status && response?.data?.data) {
          setData(response.data.data || []);
          setRowCount(response.data.pagination?.total || 0);
        } else {
          setData([]);
          setRowCount(0);
        }
      } catch (error) {
        console.error("Error fetching book list:", error);
        setData([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    },
    [
      school_id,
      apiSearch,
      pagination.pageIndex,
      pagination.pageSize,
      buildFilterPayload,
    ],
  );

  // Initial + pagination + apiSearch triggered fetch
  useEffect(() => {
    if (school_id) fetchBookList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id, pagination.pageIndex, pagination.pageSize, apiSearch]);

  // Global search Enter handler (from CustomMRTTable)
  const handleSearchEnter = useCallback((value) => {
    setApiSearch(value || "");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // ─────────────────────────────────────────────
  //  Enter key inside MRT column filter inputs → fetch
  // ─────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Enter" || e.target.tagName !== "INPUT") return;

      const isColumnFilterInput =
        e.target.closest("thead") ||
        e.target.closest(".MuiTableHead-root") ||
        e.target.closest('[class*="MRT_TableHeadCell"]') ||
        e.target.closest('[class*="Header"]');

      if (!isColumnFilterInput) return;

      e.preventDefault();
      const latestFilters = columnFiltersRef.current;

      setPagination((prev) => {
        if (prev.pageIndex === 0) {
          // already page 1 → effect won't refire, fetch directly
          fetchBookList(latestFilters);
          return prev;
        }
        return { ...prev, pageIndex: 0 }; // reset page → effect refires
      });
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [fetchBookList]);

  // ─────────────────────────────────────────────
  //  Navigation
  // ─────────────────────────────────────────────
  // AI key must exist in campus_ai_configuration (Settings → AI Key
  // Configuration) before the create page is opened; popup OK reloads.
  const [aiKeyModalOpen, setAiKeyModalOpen] = useState(false);
  const handleAdd = async () => {
    try {
      const res = await AxiosInstance.get("/settings/ai/configuration");
      if (res.data?.status && res.data?.data) {
        navigate("/user/bookCreate");
        return;
      }
    } catch (err) {
      // treated as not configured
    }
    setAiKeyModalOpen(true);
  };

  const handleView = (row) => {
    navigate("/user/bookView", { state: { ID: row.id, data: row } });
  };

  // ─────────────────────────────────────────────
  //  Columns
  //  NOTE: accessorKey values MUST match FILTER_KEY_MAP keys
  // ─────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        accessorKey: "sno",
        header: translations?.labels?.serialNo || "S.No",
        enableSorting: true,
        enableColumnFilter: false,
        size: 80,
        Cell: ({ row }) =>
          row.index + 1 + pagination.pageIndex * pagination.pageSize,
      },
      {
        accessorKey: "class",
        header: translations?.labels?.class || "Class",
        enableSorting: true,
        enableColumnFilter: true,
        size: 120,
      },
      {
        accessorKey: "subject",
        header: translations?.labels?.subject || "Subject",
        enableSorting: true,
        enableColumnFilter: true,
        size: 180,
      },
      {
        accessorKey: "medium",
        header: translations?.labels?.medium || "Medium",
        enableSorting: true,
        enableColumnFilter: true,
        size: 120,
      },
      {
        accessorKey: "term",
        header: translations?.labels?.term || "Term",
        enableSorting: true,
        enableColumnFilter: true,
        size: 120,
        Cell: ({ cell }) => cell.getValue() || "N/A",
      },
      {
        accessorKey: "volume",
        header: translations?.labels?.volume || "Volume",
        enableSorting: true,
        enableColumnFilter: true,
        size: 140,
        Cell: ({ cell }) => cell.getValue() || "N/A",
      },
      {
        accessorKey: "total_pages",
        header: translations?.labels?.totalPages || "Total Pages",
        enableSorting: true,
        enableColumnFilter: true,
        size: 130,
      },
      {
        id: "actions",
        header: translations?.labels?.action || "Option",
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={translations?.labels?.view || "View"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translations, pagination.pageIndex, pagination.pageSize],
  );

  return (
    <div className={styles.container}>
      {/* HEADER */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaBook />
        </div>
        <h1 className={styles.headerTitle}>
          {translations?.labels?.book || "Book"}
        </h1>
      </div>

      {/* ADD BUTTON */}
      <div className={styles.actions} style={{ justifyContent: "flex-end" }}>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.addButton}
          fullWidth={false}
          onClick={handleAdd}
        >
          <FaPlus style={{ marginRight: "8px" }} />
          {translations?.labels?.add || "ADD"}
        </CustomButton>
      </div>

      {/* TABLE */}
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        onSearchEnter={handleSearchEnter}
        placeholder={translations?.labels?.searchName || "Search..."}
        manualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        enableColumnFilters={true}
        noRecordsMessage={
          translations?.labels?.noRecordsToDisplay || "No records to display"
        }
      />
      <PopupModal
        open={aiKeyModalOpen}
        type="error"
        title={translations?.labels?.failed || "Failed"}
        message={
          translations?.labels?.aiKeyNotConfigured ||
          "AI Key not configured. Please configure the AI Key in Settings."
        }
        onOk={() => window.location.reload()}
        onClose={() => window.location.reload()}
      />
    </div>
  );
};

export default BookList;
